import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  CreditCard, 
  CheckCircle, 
  Clock, 
  AlertTriangle, 
  Sparkles, 
  RefreshCw, 
  FileText, 
  Settings, 
  ExternalLink,
  Download,
  Calendar,
  Zap
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API } from '../lib/utils';
 
const SubscriptionPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [managingPortal, setManagingPortal] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('annuel'); // annuel sélectionné par défaut
 
  useEffect(() => {
    setSubscribing(false);
    setManagingPortal(false);
    setLoading(true);
    fetchSubscriptionData();
  }, [token]);
 
  const fetchSubscriptionData = async () => {
    try {
      const statusResponse = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionData(statusResponse.data);
      if (statusResponse.data.stripe_customer_id) {
        try {
          const detailsResponse = await axios.get(`${API}/stripe/subscription-details`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setSubscriptionDetails(detailsResponse.data);
        } catch (err) {
          console.error('Error fetching subscription details:', err);
        }
      }
    } catch (error) {
      console.error('Error fetching subscription status:', error);
      toast.error('Erreur lors de la récupération du statut');
    }
    setLoading(false);
  };
 
  const handleSubscribe = async (plan) => {
    setSubscribing(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await axios.post(`${API}/stripe/create-checkout-session`, {
        origin_url: window.location.origin,
        plan: plan
      }, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error('Erreur: URL de paiement non reçue');
        setSubscribing(false);
      }
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        toast.error('Erreur de connexion, réessayez');
      } else {
        console.error('Error creating checkout session:', error);
        toast.error(error.response?.data?.detail || 'Erreur lors de la création de la session de paiement');
      }
      setSubscribing(false);
    }
  };
 
  const handleManageSubscription = async () => {
    setManagingPortal(true);
    try {
      const response = await axios.post(`${API}/stripe/portal-session`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        toast.error('Erreur: impossible d\'ouvrir le portail');
        setManagingPortal(false);
      }
    } catch (error) {
      console.error('Error creating portal session:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ouverture du portail');
      setManagingPortal(false);
    }
  };
 
  const formatDateFR = (isoString) => {
    if (!isoString) return '—';
    const date = new Date(isoString);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };
 
  const getCardBrandDisplay = (brand) => {
    const brands = {
      'visa': 'Visa',
      'mastercard': 'Mastercard',
      'amex': 'American Express',
      'discover': 'Discover',
      'diners': 'Diners Club',
      'jcb': 'JCB',
      'unionpay': 'UnionPay'
    };
    return brands[brand?.toLowerCase()] || brand || 'Carte';
  };
 
  if (loading) {
    return (
      <div className="page-container p-4 md:p-8">
        <div className="max-w-3xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-1/3"></div>
            <div className="h-48 bg-muted rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }
 
  const needsSubscription = subscriptionData?.subscription_status === 'expired' || 
                           subscriptionData?.subscription_status === 'canceled';
  const isTrialing = subscriptionData?.subscription_status === 'trialing';
  const isActive = subscriptionData?.subscription_status === 'active';

  // Features list (shared)
  const features = [
    'Devis et factures illimités',
    'Le client signe sur place ou depuis son téléphone',
    'PDF signé envoyé automatiquement',
    'Photos chantier incluses',
    'Envoi par email intégré',
    'Gestion clients et catalogue métier',
    'Sur chantier depuis le téléphone, au bureau depuis l\'ordinateur',
    'Pris en main en 5 minutes — sans formation'
  ];
 
  return (
    <div className="page-container p-4 md:p-8 noise-overlay">
      <div className="relative z-10 max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold text-foreground mb-8">Abonnement</h1>
 
        {/* For non-subscribed users */}
        {!isActive && (
          <div className="mb-6">

            {/* Status banner */}
            <Card className="glass-card border-border/50 mb-6">
              <CardContent className="pt-6 pb-4">
                <div className="flex items-center gap-4">
                  {isTrialing ? (
                    <Clock className="w-10 h-10 text-blue-500 flex-shrink-0" />
                  ) : (
                    <AlertTriangle className="w-10 h-10 text-red-500 flex-shrink-0" />
                  )}
                  <div>
                    <p className="font-bold text-lg">
                      {isTrialing ? 'Essai gratuit en cours' : 'Période d\'essai terminée'}
                      {isTrialing && <Badge className="bg-blue-600 text-white ml-2">Essai</Badge>}
                    </p>
                    <p className="text-muted-foreground text-sm">
                      {isTrialing && subscriptionData.days_remaining !== null &&
                        `Il vous reste ${subscriptionData.days_remaining} jour${subscriptionData.days_remaining > 1 ? 's' : ''} d'essai gratuit`
                      }
                      {needsSubscription && "Choisissez votre formule pour reprendre la création de devis et factures."}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Plan cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">

              {/* Mensuel */}
              <div
                onClick={() => setSelectedPlan('mensuel')}
                className={`relative rounded-2xl border-2 p-6 cursor-pointer transition-all ${
                  selectedPlan === 'mensuel'
                    ? 'border-primary bg-primary/10'
                    : 'border-white/10 bg-card hover:border-white/30'
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold uppercase tracking-wide">Mensuel</h3>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedPlan === 'mensuel' ? 'border-primary bg-primary' : 'border-white/30'
                  }`}>
                    {selectedPlan === 'mensuel' && <div className="w-2 h-2 bg-white rounded-full" />}
                  </div>
                </div>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-3xl font-bold">9,99€</span>
                  <span className="text-muted-foreground">/mois</span>
                </div>
                <p className="text-sm text-muted-foreground">Sans engagement · résiliable à tout moment</p>
              </div>

              {/* Annuel */}
              <div
                onClick={() => setSelectedPlan('annuel')}
                className={`relative rounded-2xl border-2 p-6 cursor-pointer transition-all ${
                  selectedPlan === 'annuel'
                    ? 'border-primary bg-primary/10'
                    : 'border-white/10 bg-card hover:border-white/30'
                }`}
              >
                {/* Badge économie */}
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                    Économisez 26%
                  </span>
                </div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold uppercase tracking-wide">Annuel</h3>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedPlan === 'annuel' ? 'border-primary bg-primary' : 'border-white/30'
                  }`}>
                    {selectedPlan === 'annuel' && <div className="w-2 h-2 bg-white rounded-full" />}
                  </div>
                </div>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-3xl font-bold">89€</span>
                  <span className="text-muted-foreground">/an</span>
                </div>
                <p className="text-sm text-muted-foreground">soit 7,42€/mois · <span className="text-green-400 font-medium">2 mois offerts</span></p>
              </div>
            </div>

            {/* Features + CTA */}
            <Card className="glass-card border-border/50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-yellow-500" />
                  <h3 className="font-bold text-lg">DevisFast Essentiel — Accès complet</h3>
                </div>
                <ul className="space-y-2 text-sm text-muted-foreground mb-6">
                  {features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>

                {isTrialing ? (
                  <Button
                    onClick={() => handleSubscribe(selectedPlan)}
                    disabled={subscribing}
                    className="w-full bg-primary hover:bg-accent text-primary-foreground h-12 font-bold uppercase tracking-wide rounded-full"
                    size="lg"
                  >
                    {subscribing ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Redirection...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <CreditCard className="w-5 h-5" />
                        {selectedPlan === 'annuel'
                          ? 'S\'abonner — 89€/an'
                          : 'S\'abonner — 9,99€/mois'}
                      </span>
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={() => handleSubscribe(selectedPlan)}
                    disabled={subscribing}
                    className="w-full bg-primary hover:bg-accent text-primary-foreground h-12 font-bold uppercase tracking-wide rounded-full"
                    size="lg"
                  >
                    {subscribing ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Redirection...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Zap className="w-5 h-5" />
                        {selectedPlan === 'annuel'
                          ? 'Activer mon abonnement — 89€/an'
                          : 'Activer mon abonnement — 9,99€/mois'}
                      </span>
                    )}
                  </Button>
                )}
                <p className="text-xs text-muted-foreground text-center mt-3">
                  🔒 Paiement sécurisé · Stripe · Sans engagement pour la formule mensuelle
                </p>
              </CardContent>
            </Card>
          </div>
        )}
 
        {/* For active subscribers */}
        {isActive && (
          <div className="space-y-6">
            <Card className="glass-card border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Statut
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Statut</span>
                  <Badge className="bg-green-600 text-white">Abonnement actif</Badge>
                </div>
                {subscriptionDetails?.subscription_start_date && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Date de début</span>
                    <span className="font-medium">{formatDateFR(subscriptionDetails.subscription_start_date)}</span>
                  </div>
                )}
                {subscriptionDetails?.next_renewal_date && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Prochain renouvellement</span>
                    <span className="font-medium flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 text-blue-400" />
                      {formatDateFR(subscriptionDetails.next_renewal_date)}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Prix</span>
                  <span className="text-4xl font-bold text-foreground">9,99€</span>
                </div>
              </CardContent>
            </Card>
 
            <Card className="glass-card border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-blue-400" />
                  Moyen de paiement
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {subscriptionDetails?.card_last4 ? (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Carte</span>
                      <span className="font-medium flex items-center gap-2">
                        <CreditCard className="w-4 h-4" />
                        {getCardBrandDisplay(subscriptionDetails.card_brand)} •••• {subscriptionDetails.card_last4}
                      </span>
                    </div>
                    {subscriptionDetails.card_exp_month && subscriptionDetails.card_exp_year && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Expiration</span>
                        <span className="font-medium">
                          {String(subscriptionDetails.card_exp_month).padStart(2, '0')}/{subscriptionDetails.card_exp_year}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-muted-foreground text-sm">Aucune carte enregistrée</p>
                )}
                <Button
                  onClick={handleManageSubscription}
                  disabled={managingPortal}
                  variant="outline"
                  size="sm"
                  className="mt-2"
                >
                  {managingPortal ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                      Chargement...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4" />
                      Modifier ma carte
                    </span>
                  )}
                </Button>
              </CardContent>
            </Card>
 
            <Card className="glass-card border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-400" />
                  Historique des factures
                </CardTitle>
              </CardHeader>
              <CardContent>
                {subscriptionDetails?.invoices && subscriptionDetails.invoices.length > 0 ? (
                  <div className="space-y-3">
                    {subscriptionDetails.invoices.map((invoice) => (
                      <div
                        key={invoice.id}
                        className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm">{formatDateFR(invoice.date)}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="font-medium">{invoice.amount?.toFixed(2)}€</span>
                          {invoice.pdf_url && (
                            <a
                              href={invoice.pdf_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-400 hover:text-blue-300 flex items-center gap-1 text-sm"
                            >
                              <Download className="w-4 h-4" />
                              PDF
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm py-4 text-center">
                    Aucune facture disponible
                  </p>
                )}
              </CardContent>
            </Card>
 
            <Card className="glass-card border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Settings className="w-5 h-5 text-gray-400" />
                  Gestion de l'abonnement
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  onClick={handleManageSubscription}
                  disabled={managingPortal}
                  className="w-full"
                  variant="outline"
                >
                  {managingPortal ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                      Redirection...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <ExternalLink className="w-4 h-4" />
                      Gérer mon abonnement
                    </span>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground/70 text-center pt-2">
                  Abonnement sans engagement. Résiliation possible à tout moment.
                  Aucun remboursement partiel de la période en cours.
                </p>
              </CardContent>
            </Card>
          </div>
        )}
 
        <div className="mt-8 text-center">
          <Button variant="ghost" onClick={() => navigate('/dashboard')}>
            Retour au tableau de bord
          </Button>
        </div>
      </div>
    </div>
  );
};
 
export default SubscriptionPage;
